/* eslint-disable no-shadow */
import React, { useRef, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import mapboxgl from 'mapbox-gl';
import d3 from '../../utils/d3Importer';

const MAPBOX_ACCESS_TOKEN =
  'pk.eyJ1Ijoic2Jzb25saW5lIiwiYSI6IklRMkRCVVEifQ.ldQz-kg5lTylBG6J4HoXbg';

export default function Map() {
  const mapContainer = useRef();
  const mapWrap = useRef();
  const popup = useRef(
    new mapboxgl.Popup({
      closeButton: true,
      closeOnClick: true,
    }),
  );

  const colorScaleSingle = d3
    .scaleOrdinal()
    .domain([-1, 0, 1, 2, 3, 4])
    .range(['#ffffff', '#ffffcc', '#a1dab4', '#41b6c4', '#2c7fb8', '#253494']);

  const colorScaleDouble = d3
    .scaleOrdinal()
    .domain([-1, 0, 1, 2, 3, 4])
    .range(['#ffffff', '#ffffcc', '#c2e699', '#78c679', '#31a354', '#006837']);

  const colorBooster = d3
    .scaleOrdinal()
    .domain([-1, 0, 1, 2, 3, 4])
    .range(['#ffffff', '#ffffb2', '#fecc5c', '#fd8d3c', '#f03b20', '#bd0026']);

  const mapType = useSelector((state) => state.data.mapType);
  const data = useSelector((state) => state.data.data);

  mapboxgl.accessToken = MAPBOX_ACCESS_TOKEN;

  const [map, setMap] = useState(null);
  const [mapLoaded, setMapLoaded] = useState(false);

  useEffect(() => {
    const initializeMap = ({ setMap, mapContainer }) => {
      const map = new mapboxgl.Map({
        container: mapContainer.current,
        // style: 'data/style.json',
        style: 'mapbox://styles/sbsonline/ckz4w6aus000t14mp6dbeb1xm',
        center: window.innerWidth < 600 ? [18, 3] : [1, 3],
        zoom: window.innerWidth < 600 ? 0 : 0.8,
        pitch: 0,
        bearing: 0,
        minZoom: 0,
        maxZoom: 7,
      });

      map.addControl(new mapboxgl.NavigationControl({ showCompass: false }));
      map.dragRotate.disable();
      // map.touchZoomRotate.disableRotation();
      // map.scrollZoom.disable();

      map.on('load', () => {
        setMap(map);
        setMapLoaded(true);
      });
    };

    if (!map) initializeMap({ setMap, mapContainer });
  }, [map]);

  const parseTime = d3.timeParse('%Y-%m-%d');
  const formatTime = d3.timeFormat('%-d %B %Y');
  function parseDate(date) {
    return formatTime(parseTime(date));
  }

  useEffect(() => {
    const onMapClick = (e) => {
      map.getCanvas().style.cursor = 'pointer';
      console.log(e);
      const coordinates = e.features[0].geometry.coordinates.slice();
      const pointProperties = e.features[0].properties;

      while (Math.abs(e.lngLat.lng - coordinates[0]) > 180) {
        coordinates[0] += e.lngLat.lng > coordinates[0] ? 360 : -360;
      }

      function getPopupHTML(pointProps) {
        const countryData = data.find((obj) => {
          return pointProps.ISO_A2 === obj.iso2;
        });
        console.log('pointProps ', pointProps);
        console.log('countryData ', countryData);

        let htmlPop;
        if (countryData) {
          const boostersPH =
            countryData.total_boosters_per_hundred !== ''
              ? countryData.total_boosters_per_hundred
              : '0';

          htmlPop = '<div class="htmlPopWrap">';

          htmlPop += '<div class="row-section-single">';
          htmlPop += `<div class="pop-location">${countryData.location}</div>`;
          htmlPop += '</div>';

          htmlPop += '<div class="row-section-single">';
          htmlPop += `<div class="pop-label-date">Date Recorded: ${parseDate(
            countryData.date,
          )}</div>`;
          htmlPop += '</div>';

          htmlPop += `<div class="row-section" style="border-bottom: 3px solid ${colorScaleSingle(
            countryData.singleMap,
          )}">`;
          htmlPop += '<div class="pop-label">One vaccination</div>';
          htmlPop += `<div class="pop-value">${countryData.people_vaccinated_per_hundred}%</div>`;
          htmlPop += '</div>';

          htmlPop += `<div class="row-section" style="border-bottom: 3px solid ${colorScaleDouble(
            countryData.doubleMap,
          )}">`;
          htmlPop += '<div class="pop-label">Two vaccinations</div>';
          htmlPop += `<div class="pop-value">${countryData.people_fully_vaccinated_per_hundred}%</div>`;
          htmlPop += '</div>';

          htmlPop += `<div class="row-section" style="border-bottom: 3px solid ${colorBooster(
            countryData.boosterMap,
          )}">`;
          htmlPop += '<div class="pop-label">Boosters</div>';
          htmlPop += `<div class="pop-value">${boostersPH}%</div>`;
          htmlPop += '</div>';

          htmlPop += '</div>';
        } else {
          htmlPop = '<div class="htmlPopWrap">';

          htmlPop += '<div class="row-section-single">';
          htmlPop += `<div class="pop-location">${pointProps.NAME}</div>`;
          htmlPop += '</div>';

          htmlPop += '<div class="row-section-single">';
          htmlPop += '<div class="pop-nodata">No data for this location</div>';
          htmlPop += '</div>';

          htmlPop += '</div>';
        }

        return htmlPop;
      }

      const maxWidth = window.innerWidth < 600 ? '280px' : '440px';

      popup.current
        .setLngLat(e.lngLat)
        .setHTML(getPopupHTML(pointProperties))
        .setMaxWidth(maxWidth)
        .addTo(map);

      function closePopUp() {
        popup.current.off('close', closePopUp);
      }
      popup.current.on('close', closePopUp);
    };

    if (mapLoaded) {
      // click events...
      map.on('click', 'country-bg', onMapClick);
    }

    return () => {
      if (mapLoaded) {
        map.off('click', 'country-bg', onMapClick);
      }
    };
  }, [mapLoaded]);

  useEffect(() => {
    if (mapLoaded && data.length > 0) {
      const singleTargets0 = [];
      const singleTargets1 = [];
      const singleTargets2 = [];
      const singleTargets3 = [];
      const singleTargets4 = [];

      const singleTargets = [
        singleTargets0,
        singleTargets1,
        singleTargets2,
        singleTargets3,
        singleTargets4,
      ];

      const doubleTargets0 = [];
      const doubleTargets1 = [];
      const doubleTargets2 = [];
      const doubleTargets3 = [];
      const doubleTargets4 = [];

      const doubleTargets = [
        doubleTargets0,
        doubleTargets1,
        doubleTargets2,
        doubleTargets3,
        doubleTargets4,
      ];

      const tripleTargets0 = [];
      const tripleTargets1 = [];
      const tripleTargets2 = [];
      const tripleTargets3 = [];
      const tripleTargets4 = [];

      const tripleTargets = [
        tripleTargets0,
        tripleTargets1,
        tripleTargets2,
        tripleTargets3,
        tripleTargets4,
      ];

      if (mapType === 1) {
        data.forEach((obj) => {
          singleTargets.forEach((target, index) => {
            if (obj.singleMap === index) {
              target.push(obj.iso2);
            }
          });
        });

        singleTargets.forEach((arr, index) => {
          map.setPaintProperty(`single-${index}`, 'fill-opacity', [
            'match',
            ['get', 'ISO_A2'],
            arr,
            1,
            0,
          ]);
        });

        doubleTargets.forEach((arr, index) => {
          map.setPaintProperty(`double-${index}`, 'fill-opacity', 0);
        });

        tripleTargets.forEach((arr, index) => {
          map.setPaintProperty(`boosters-${index}`, 'fill-opacity', 0);
        });
      }

      if (mapType === 2) {
        data.forEach((obj) => {
          doubleTargets.forEach((target, index) => {
            if (obj.doubleMap === index) {
              target.push(obj.iso2);
            }
          });
        });

        singleTargets.forEach((arr, index) => {
          map.setPaintProperty(`single-${index}`, 'fill-opacity', 0);
        });

        doubleTargets.forEach((arr, index) => {
          map.setPaintProperty(`double-${index}`, 'fill-opacity', [
            'match',
            ['get', 'ISO_A2'],
            arr,
            1,
            0,
          ]);
        });

        tripleTargets.forEach((arr, index) => {
          map.setPaintProperty(`boosters-${index}`, 'fill-opacity', 0);
        });
      }

      if (mapType === 3) {
        data.forEach((obj) => {
          tripleTargets.forEach((target, index) => {
            if (obj.boosterMap === index) {
              target.push(obj.iso2);
            }
          });
        });

        singleTargets.forEach((arr, index) => {
          map.setPaintProperty(`single-${index}`, 'fill-opacity', 0);
        });

        doubleTargets.forEach((arr, index) => {
          map.setPaintProperty(`double-${index}`, 'fill-opacity', 0);
        });

        tripleTargets.forEach((arr, index) => {
          if (arr.length > 0) {
            map.setPaintProperty(`boosters-${index}`, 'fill-opacity', [
              'match',
              ['get', 'ISO_A2'],
              arr,
              1,
              0,
            ]);
          } else {
            map.setPaintProperty(`boosters-${index}`, 'fill-opacity', 0);
          }
        });
      }
    }
  }, [mapLoaded, mapType, data]);

  // render
  return (
    <div id="map-wrap" ref={mapWrap}>
      <div ref={mapContainer} className="mapContainer" />
    </div>
  );
}
